import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Tracks the content width of an element (via ResizeObserver) so SVG charts
 * can lay out in real pixels instead of scaling their text. Returns
 * `fallback` until the element has been measured, and in environments
 * without layout (tests).
 */
export function useElementWidth<T extends HTMLElement>(fallback: number): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => {
      const measured = Math.floor(element.clientWidth);
      if (measured > 0) setWidth(measured);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
